// auth.hooks.ts
import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { toast } from '@/shared/hooks/use-toast';
import { LoginPayload } from '../model';
import { authKeys } from '../utils/auth.keys';
import {
  authApi,
  ForgotPasswordPayload,
  OAuthProvider,
  ResetPasswordPayload,
  SessionWebPayload,
} from '../api/auth.api';

// Hook para login
export function useLogin(options?: UseMutationOptions<any, Error, LoginPayload>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...authKeys.all, 'login'],
    mutationFn: authApi.login,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: authKeys.all });
      toast({
        title: 'Bem-vindo de volta!',
        description: 'Login realizado com sucesso.',
        variant: 'success',
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro na autenticação',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}

// Hook para iniciar login social (redireciona para o provedor)
export function useOAuthRedirect(options?: UseMutationOptions<void, Error, OAuthProvider>) {
  return useMutation({
    mutationKey: [...authKeys.all, 'oauth-redirect'],
    mutationFn: async (provider: OAuthProvider) => {
      const { url } = await authApi.getOAuthUrl(
        provider,
        `${window.location.origin}/auth/callback`,
      );
      window.location.assign(url);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro na autenticação',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}

// Hook para criar sessão web a partir dos tokens OAuth
export function useSessionWeb(options?: UseMutationOptions<any, Error, SessionWebPayload>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...authKeys.all, 'session-web'],
    mutationFn: authApi.sessionWeb,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: authKeys.all });
      toast({
        title: 'Bem-vindo de volta!',
        description: 'Login realizado com sucesso.',
        variant: 'success',
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro na autenticação',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}

// Hook para logout
export function useLogout(options?: UseMutationOptions<void, Error, void>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...authKeys.all, 'logout'],
    mutationFn: authApi.logout,
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.clear();
      toast({
        title: 'Logout realizado com sucesso',
        variant: 'success',
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao fazer logout',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}

// Hook para forgot password
export function useForgotPassword(options?: UseMutationOptions<any, Error, ForgotPasswordPayload>) {
  return useMutation({
    mutationKey: [...authKeys.all, 'forgot-password'],
    mutationFn: authApi.forgotPassword,
    onSuccess: (data, variables, onMutateResult, context) => {
      toast({
        title: 'E-mail enviado com sucesso',
        description: 'Verifique sua caixa de entrada para redefinir sua senha.',
        variant: 'success',
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao enviar e-mail',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}

// Hook para reset password
export function useResetPassword(options?: UseMutationOptions<any, Error, ResetPasswordPayload>) {
  return useMutation({
    mutationKey: [...authKeys.all, 'reset-password'],
    mutationFn: authApi.resetPassword,
    onSuccess: (data, variables, onMutateResult, context) => {
      toast({
        title: 'Senha alterada com sucesso',
        description: 'Sua senha foi redefinida. Você já pode fazer login.',
        variant: 'success',
      });
      options?.onSuccess?.(data, variables, onMutateResult, context);
    },
    onError: (error, variables, onMutateResult, context) => {
      toast({
        title: 'Erro ao redefinir senha',
        description: error.message,
        variant: 'destructive',
      });
      options?.onError?.(error, variables, onMutateResult, context);
    },
    ...options,
  });
}